/**
 * Month grid — SPEC §9.1, §12.2.
 *
 * Uses the shared calendar module for every date decision, so what the instructor sees and
 * what the server stores come from the same code (SPEC §9.1). No `new Date()` arithmetic
 * anywhere in this file.
 *
 * Availability is never signaled by color alone: an available day also carries a ✓ and an
 * accessible label, and a past day is striped as well as grayed (SPEC §12.2).
 */

import {
  compare,
  datesInCycle,
  dayOfWeekIndex,
  isOnLeaveOn,
  isProvisional,
  parseCycleKey,
  parseISO,
  toISO,
  type CalendarDate,
  type HorizonContext,
  type LeaveSnapshot,
} from '@gearup/shared';
import type { Occurrence } from '../lib/api.js';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const WEEKDAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export interface MonthCalendarProps {
  cycleKey: string;
  occurrences: Occurrence[];
  /** Today in the instructor's own timezone (SPEC §3.5) — supplied by the server. */
  today: CalendarDate;
  /**
   * The instructor's confirmation horizon as it applies to THIS month (SPEC §3.3). Days
   * after it are shown as provisional rather than confirmed.
   *
   * Without this the calendar contradicted itself: an instructor who said "I'm only sure
   * through the 15th" while their pattern still filled the rest of the month saw solid
   * green ticks on the 16th and 19th, sitting directly above a note saying they were not
   * sure about those days. The data was right; the calendar just refused to show the
   * difference.
   *
   * Built by `horizonContextOf`, so the §3.3.1 override is applied once on the server
   * rather than re-derived here where it could drift.
   */
  horizon: HorizonContext;
  /**
   * Breaks touching this month (SPEC §3.7).
   *
   * A day inside one is not cover, and the coverage grid excludes it — but the day itself is
   * deliberately left marked, because nothing rewrites somebody's availability on their
   * behalf (§3.3.2) and it all comes back when the break ends. Which meant a day inside a
   * break looked exactly like a day that counts. Luke read a screen full of green ticks
   * across a fortnight he had booked off.
   */
  leaves?: readonly LeaveSnapshot[];
  onToggle: (date: string, isAvailable: boolean) => void;
  disabled?: boolean;
}

export function MonthCalendar({
  cycleKey,
  occurrences,
  today,
  horizon,
  leaves = [],
  onToggle,
  disabled = false,
}: MonthCalendarProps) {
  const { year, month } = parseCycleKey(cycleKey);
  const days = datesInCycle(cycleKey);

  // A date can hold more than one slot (two locations on one Saturday), so this maps to a
  // list rather than a single occurrence.
  const byDate = new Map<string, Occurrence[]>();
  for (const o of occurrences) {
    const list = byDate.get(o.date) ?? [];
    list.push(o);
    byDate.set(o.date, list);
  }

  // Leading blanks so the 1st lands under the right weekday column.
  const leadingBlanks = dayOfWeekIndex(days[0]!);

  return (
    <div className="calendar">
      <div className="calendar__weekdays" aria-hidden="true">
        {WEEKDAY_INITIALS.map((initial, i) => (
          <div key={i}>{initial}</div>
        ))}
      </div>

      <div
        className="calendar__grid"
        role="group"
        aria-label={`${MONTH_NAMES[month - 1]} ${year} availability`}
      >
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <div key={`blank-${i}`} className="day day--blank" aria-hidden="true" />
        ))}

        {days.map((day) => (
          <DayCell
            key={toISO(day)}
            day={day}
            slots={byDate.get(toISO(day)) ?? []}
            isPast={compare(day, today) < 0}
            isProvisional={isProvisional(toISO(day), horizon)}
            onBreak={isOnLeaveOn(toISO(day), leaves)}
            disabled={disabled}
            onToggle={onToggle}
          />
        ))}
      </div>

      <div className="legend">
        <span className="legend__item">
          <span className="legend__swatch legend__swatch--available" aria-hidden="true" />
          <span>✓ Available</span>
        </span>
        {horizon.horizon && (
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--provisional" aria-hidden="true" />
            <span>Not confirmed yet</span>
          </span>
        )}
        <span className="legend__item">
          <span className="legend__swatch" aria-hidden="true" />
          <span>Not available</span>
        </span>
        {leaves.length > 0 && (
          <span className="legend__item">
            <span className="legend__swatch legend__swatch--break" aria-hidden="true" />
            <span>Inside your break</span>
          </span>
        )}
        <span className="legend__item">
          <span className="legend__swatch legend__swatch--past" aria-hidden="true" />
          <span>Already passed</span>
        </span>
      </div>
    </div>
  );
}

function DayCell({
  day,
  slots,
  isPast,
  isProvisional,
  onBreak,
  disabled,
  onToggle,
}: {
  day: CalendarDate;
  slots: Occurrence[];
  isPast: boolean;
  isProvisional: boolean;
  onBreak: boolean;
  disabled: boolean;
  onToggle: (date: string, isAvailable: boolean) => void;
}) {
  const iso = toISO(day);
  const isAvailable = slots.length > 0;
  const weekday = WEEKDAY_NAMES[dayOfWeekIndex(day)];
  const label = `${weekday} ${MONTH_NAMES[day.month - 1]} ${day.day}`;

  if (isPast) {
    return (
      <div className="day day--past" aria-disabled="true" aria-label={`${label} — already passed`}>
        {day.day}
      </div>
    );
  }

  // A day can hold more than one class — Donald teaches a morning and an afternoon at the
  // same site. Showing only `slots[0]` made the second one invisible on the calendar, which
  // is the one place an instructor checks to see whether we got it right.
  const first = slots[0];
  const time = first?.startTime ?? (first?.period === 'ALL_DAY' ? '' : (first?.period ?? ''));
  const extra = slots.length - 1;

  const provisional = isAvailable && isProvisional;
  // A break outranks the horizon in what it tells the reader: "not confirmed yet" invites
  // confirming, and there is nothing to confirm on a day you are away.
  const state = onBreak
    ? isAvailable
      ? 'marked, but you are on a break that day'
      : 'inside your break'
    : provisional
      ? 'marked but not confirmed yet'
      : isAvailable
        ? slots.length > 1
          ? `${slots.length} classes`
          : 'available'
        : 'not available';

  return (
    <button
      type="button"
      className={`day${isAvailable ? ' day--available' : ''}${provisional ? ' day--provisional' : ''}${onBreak ? ' day--break' : ''}`}
      onClick={() => onToggle(iso, !isAvailable)}
      disabled={disabled}
      aria-pressed={isAvailable}
      aria-label={`${label} — ${state}`}
    >
      <span>{day.day}</span>
      {/* Icon as well as color, so the state survives colorblindness and grayscale.
          A provisional day gets "?" rather than "✓" — the distinction must not rely on
          the dashed border alone (SPEC §12.2). */}
      {/* Icon, not color alone (SPEC §12.2). A pause mark on a day inside a break says the
          same thing the notice above the calendar says, where the reader is looking. */}
      {(isAvailable || onBreak) && (
        <span className="day__mark" aria-hidden="true">
          {onBreak ? '⏸' : provisional ? '?' : '✓'}
        </span>
      )}
      {isAvailable && (time || extra > 0) && (
        <span className="day__time" aria-hidden="true">
          {time}
          {extra > 0 && <span className="day__more">+{extra}</span>}
        </span>
      )}
    </button>
  );
}

/** `2026-03-07` → `Saturday, March 7`. Formatting stays out of the calendar grid itself. */
export function formatDayLong(iso: string): string {
  const date = parseISO(iso);
  return `${WEEKDAY_NAMES[dayOfWeekIndex(date)]}, ${MONTH_NAMES[date.month - 1]} ${date.day}`;
}
